import { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  Folder as FolderIcon,
  LayoutGrid,
  ListFilter,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Lightweight filter for the My Learning grid. Sits beside the search
 * bar and lets users narrow the listing to folders only, lessons only,
 * or everything.
 *
 * Designed as a registry of filters so future facets (tags, status,
 * etc.) can be added as new entries without touching the call site.
 * Today there's just the "view" facet.
 */

export type ViewFilterValue = 'all' | 'folders' | 'lessons';

interface Option {
  value: ViewFilterValue;
  label: string;
  description: string;
  icon: typeof LayoutGrid;
}

const OPTIONS: readonly Option[] = [
  {
    value: 'all',
    label: 'All',
    description: 'Folders and lessons together',
    icon: LayoutGrid,
  },
  {
    value: 'folders',
    label: 'Folders',
    description: 'Hide individual lessons',
    icon: FolderIcon,
  },
  {
    value: 'lessons',
    label: 'Lessons',
    description: 'Hide folders',
    icon: BookOpen,
  },
] as const;

interface ViewFilterProps {
  value: ViewFilterValue;
  onChange: (next: ViewFilterValue) => void;
  /** Disable while the page is mid-fetch so the user doesn't fire many
   *  filter changes in flight at once. */
  disabled?: boolean;
}

export function ViewFilter({ value, onChange, disabled }: ViewFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const active = OPTIONS.find(o => o.value === value) ?? OPTIONS[0];
  const isFiltered = value !== 'all';

  return (
    <div ref={containerRef} className='relative inline-block max-md:static'>
      <button
        type='button'
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        title='Filter view'
        className={cn(
          'flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors sm:h-10',
          'border-border bg-card text-foreground',
          'hover:bg-muted dark:hover:bg-accent',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:focus-visible:ring-primary/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isFiltered &&
            'dark:tint-brand border-brand-300 bg-brand-50/80 text-brand-700 hover:bg-brand-100 dark:border-primary/60 dark:hover:bg-primary/20'
        )}
      >
        <ListFilter className='h-4 w-4 flex-shrink-0' />
        <span>{active.label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
            isOpen ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {isOpen && (
        <div
          role='listbox'
          className={cn(
            // Below `md` the trigger is `static`, so this anchors to the
            // header's filter row and spans it - a right-anchored 260px box
            // off a left-hand chip ran off the left edge of a phone.
            'absolute top-full z-50 mt-2 rounded-2xl border shadow-lg max-md:inset-x-0 md:right-0 md:w-[260px]',
            'border-border bg-card'
          )}
        >
          <div className='space-y-1 p-1.5'>
            {OPTIONS.map(option => {
              const isActive = option.value === value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  role='option'
                  aria-selected={isActive}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors max-sm:min-h-11',
                    !isActive && 'hover:bg-muted dark:hover:bg-accent',
                    isActive && 'bg-brand-50/70 dark:bg-tint-brand'
                  )}
                >
                  <span
                    className={cn(
                      // Selection changes only the icon COLOUR; circle bg constant.
                      'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted',
                      isActive
                        ? 'text-brand-600 dark:text-primary'
                        : 'text-muted-foreground'
                    )}
                  >
                    <Icon className='h-3.5 w-3.5' />
                  </span>
                  <div className='min-w-0 flex-1'>
                    <div
                      className={cn(
                        'text-sm font-semibold',
                        isActive
                          ? 'text-brand-700 dark:text-tint-brand-foreground'
                          : 'text-foreground'
                      )}
                    >
                      {option.label}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {option.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
